/**
 * Serviço de Sincronização em Background - JurisPrazo V2
 * Solução híbrida para sincronização quando usuário não está online
 */

import { SyncService } from './syncService';
import { FirebaseService } from './firebaseService';

class BackgroundSyncService {
  constructor() {
    this.isServiceWorkerSupported = 'serviceWorker' in navigator;
    this.isBackgroundSyncSupported = 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype;
    this.pendingSyncs = new Map();
    
    this.init();
  }

  async init() {
    if (this.isServiceWorkerSupported) {
      try {
        // Registrar Service Worker para background sync
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('✅ Service Worker registrado para background sync');
        
        // Configurar listeners
        this.setupServiceWorkerListeners();
      } catch (error) {
        console.warn('⚠️ Service Worker não pôde ser registrado:', error);
      }
    }

    // Fallback: Detectar quando usuário volta online
    this.setupOnlineDetection();
  }

  /**
   * Agendar sincronização em background
   */
  async scheduleBackgroundSync(userId, syncType = 'daily') {
    const syncData = {
      userId,
      syncType,
      scheduledAt: Date.now(),
      attempts: 0
    };

    // Tentar background sync nativo primeiro
    if (this.isBackgroundSyncSupported) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register(`sync-${userId}-${syncType}`);
        
        // Salvar dados da sync para o service worker
        localStorage.setItem(`pendingSync-${userId}`, JSON.stringify(syncData));
        
        console.log(`📱 Background sync agendado para usuário ${userId}`);
        return true;
      } catch (error) {
        console.warn('⚠️ Background sync nativo falhou:', error);
      }
    }

    // Fallback: Agendar para quando usuário voltar online
    this.pendingSyncs.set(userId, syncData);
    this.saveToLocalStorage();
    
    console.log(`💾 Sync agendado em localStorage para usuário ${userId}`);
    return true;
  }

  /**
   * Configurar listeners do Service Worker
   */
  setupServiceWorkerListeners() {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'SYNC_COMPLETED') {
        console.log('✅ Background sync completado:', event.data);
        this.cleanupCompletedSync(event.data.userId);
      } else if (event.data.type === 'SYNC_FAILED') {
        console.error('❌ Background sync falhou:', event.data);
        this.handleSyncFailure(event.data.userId, event.data.error);
      }
    });
  }

  /**
   * Detectar quando usuário volta online
   */
  setupOnlineDetection() {
    // Executar syncs pendentes quando voltar online
    window.addEventListener('online', async () => {
      console.log('🌐 Usuário voltou online - verificando syncs pendentes');
      await this.executePendingSyncs();
    });

    // Executar syncs pendentes quando página carregar
    document.addEventListener('DOMContentLoaded', async () => {
      if (navigator.onLine) {
        await this.executePendingSyncs();
      }
    });

    // Executar syncs pendentes quando página ganhar foco
    window.addEventListener('focus', async () => {
      if (navigator.onLine) {
        await this.executePendingSyncs();
      }
    });
  }

  /**
   * Executar sincronizações pendentes
   */
  async executePendingSyncs() {
    // Carregar syncs do localStorage
    this.loadFromLocalStorage();

    if (this.pendingSyncs.size === 0) {
      return;
    }

    console.log(`🔄 Executando ${this.pendingSyncs.size} syncs pendentes`);

    for (const [userId, syncData] of this.pendingSyncs.entries()) {
      try {
        // Verificar se não passou muito tempo (máximo 24h)
        const hoursAgo = (Date.now() - syncData.scheduledAt) / (1000 * 60 * 60);
        if (hoursAgo > 24) {
          console.log(`⏰ Sync muito antiga para usuário ${userId} (${hoursAgo.toFixed(1)}h) - removendo`);
          this.pendingSyncs.delete(userId);
          continue;
        }

        // Verificar se usuário ainda tem configuração ativa
        const settings = await FirebaseService.getMonitoringSettings(userId);
        if (!settings || !settings.autoSync) {
          console.log(`⚠️ Sync desabilitado para usuário ${userId} - removendo`);
          this.pendingSyncs.delete(userId);
          continue;
        }

        // Executar sincronização
        console.log(`🔄 Executando sync pendente para usuário ${userId}`);
        await this.executeSyncForUser(userId, settings);
        
        // Remover da lista de pendentes
        this.pendingSyncs.delete(userId);
        
        console.log(`✅ Sync pendente executado para usuário ${userId}`);

        // Delay entre usuários para não sobrecarregar
        await this.delay(2000);

      } catch (error) {
        console.error(`❌ Erro na sync pendente para usuário ${userId}:`, error);
        
        // Incrementar tentativas
        syncData.attempts = (syncData.attempts || 0) + 1;
        
        // Remover se muitas tentativas
        if (syncData.attempts >= 3) {
          console.log(`❌ Removendo sync após ${syncData.attempts} tentativas para usuário ${userId}`);
          this.pendingSyncs.delete(userId);
        }
      }
    }

    // Salvar estado atualizado
    this.saveToLocalStorage();
  }

  /**
   * Executar sincronização para um usuário específico
   */
  async executeSyncForUser(userId, settings) {
    // Buscar OABs do usuário
    const oabs = await FirebaseService.getUserOABs(userId);
    
    if (oabs.length === 0) {
      throw new Error('Nenhuma OAB encontrada');
    }

    // Executar sincronização diária (últimos 3 dias)
    await SyncService.executeDailySync(userId, oabs);
  }

  /**
   * Salvar estado no localStorage
   */
  saveToLocalStorage() {
    const data = Array.from(this.pendingSyncs.entries());
    localStorage.setItem('pendingBackgroundSyncs', JSON.stringify(data));
  }

  /**
   * Carregar estado do localStorage
   */
  loadFromLocalStorage() {
    try {
      const data = localStorage.getItem('pendingBackgroundSyncs');
      if (data) {
        const entries = JSON.parse(data);
        this.pendingSyncs = new Map(entries);
      }
    } catch (error) {
      console.warn('⚠️ Erro ao carregar syncs pendentes:', error);
      this.pendingSyncs = new Map();
    }
  }

  /**
   * Limpar sync completada
   */
  cleanupCompletedSync(userId) {
    this.pendingSyncs.delete(userId);
    this.saveToLocalStorage();
    
    // Limpar também do localStorage do service worker
    localStorage.removeItem(`pendingSync-${userId}`);
  }

  /**
   * Tratar falha de sync
   */
  handleSyncFailure(userId, error) {
    const syncData = this.pendingSyncs.get(userId);
    if (syncData) {
      syncData.attempts = (syncData.attempts || 0) + 1;
      syncData.lastError = error;
      
      if (syncData.attempts >= 3) {
        this.pendingSyncs.delete(userId);
      }
      
      this.saveToLocalStorage();
    }
  }

  /**
   * Obter estatísticas das syncs pendentes
   */
  getStats() {
    return {
      pendingCount: this.pendingSyncs.size,
      pendingUsers: Array.from(this.pendingSyncs.keys()),
      isServiceWorkerSupported: this.isServiceWorkerSupported,
      isBackgroundSyncSupported: this.isBackgroundSyncSupported,
      isOnline: navigator.onLine
    };
  }

  /**
   * Delay helper
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Instância singleton
const backgroundSyncService = new BackgroundSyncService();

export default backgroundSyncService;
